import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";
import { Link } from "react-router-dom";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";

import { FindSimilarIcon, HeartIcon, PlusIcon } from "./productTileIcons";

export interface EvaluateProductTileProps {
  id: string;
  name: string;
  brandName: string;
  imageUrl: string;
  price: string;
  msrp?: string;
  minOrder?: string;
  caseSize?: number;
  freeShipping?: boolean;
  freeShippingThreshold?: string;
  isTopShop?: boolean;
  isBestseller?: boolean;
  isNew?: boolean;
  rating?: number;
  reviewCount?: number;
  urgency?: string;
  /** Preorder ship date line below the case size, e.g. "Ships now" or "Ships Feb 15 - 20". */
  shipDate?: string;
  promoted?: boolean;
  hideWholesalePrice?: boolean;
  blurWholesalePrice?: boolean;
  compact?: boolean;
  onClick?: () => void;
  onQuickAdd?: () => void;
  /** When set, heart button calls this (e.g. add to staging area); when true, heart appears filled */
  onHeartClick?: () => void;
  isHearted?: boolean;
  /** When set, the Find similar button links to the Similar products page with this product as anchor */
  findSimilarTo?: {
    productId: string;
    name: string;
    imageUrl: string;
    price: string;
    msrp?: string;
    brandName: string;
  };
  /** Hide the "Find similar" button (e.g. contexts where cross-brand discovery doesn't apply). */
  hideFindSimilar?: boolean;
  /**
   * `cart`: Cart redesign “Retailer Tile Evaluate Product” — quick-add on image, heart
   * always visible, compact price + name + optional subdued line; no find similar.
   */
  variant?: "default" | "cart";
  /** e.g. “Case of 1” — below the title (with `variant="cart"`) */
  subduedDetail?: string;
  className?: string;
  /** When set, product tile links here instead of `/product/:id`. */
  detailPath?: string;
  /** "square" (default) fits most product photography; "portrait" (2:3)
   * fits full-body model shots (e.g. apparel brands) that crop badly into a
   * square. */
  aspectRatio?: "square" | "portrait";
  /** Extra className applied to the product `<img>` itself (e.g. a tint
   * overlay) — separate from `className`, which applies to the outer tile. */
  imageClassName?: string;
}

export default function EvaluateProductTile({
  id,
  name,
  brandName,
  imageUrl,
  price,
  msrp,
  minOrder,
  caseSize = 1,
  freeShipping = false,
  freeShippingThreshold,
  isBestseller = false,
  isNew = false,
  rating,
  reviewCount,
  urgency,
  shipDate,
  promoted = false,
  hideWholesalePrice = false,
  blurWholesalePrice = false,
  compact = false,
  onClick,
  onQuickAdd,
  onHeartClick,
  isHearted = false,
  findSimilarTo,
  hideFindSimilar = false,
  variant = "default",
  subduedDetail,
  className = "",
  detailPath,
  aspectRatio = "square",
  imageClassName,
}: EvaluateProductTileProps) {
  const isCartTile = variant === "cart";
  const productUrl = detailPath ?? `/product/${id}`;
  const msrpDisplay =
    msrp != null && !String(msrp).startsWith("$") ? `$${msrp}` : msrp;
  const { src: resolvedImageSrc, onError: onProductImageError } =
    usePlaygroundImageWithFallback(imageUrl);
  const findSimilarHref = findSimilarTo
    ? `/search/find-similar?productId=${encodeURIComponent(findSimilarTo.productId)}&name=${encodeURIComponent(findSimilarTo.name)}&image=${encodeURIComponent(findSimilarTo.imageUrl)}&price=${encodeURIComponent(findSimilarTo.price)}&msrp=${encodeURIComponent(findSimilarTo.msrp ?? "")}&brand=${encodeURIComponent(findSimilarTo.brandName)}`
    : null;

  return (
    <div
      className={cn("group relative flex h-full flex-col", className)}
      data-test-id="product-tile"
    >
      {/* ── Image: rounded, off-white background ── */}
      <div
        className="relative overflow-hidden rounded"
        style={{ backgroundColor: "#f7f7f7" }}
      >
        <Link
          to={productUrl}
          data-test-id="product-tile-link"
          onClick={onClick}
        >
          <picture
            className="relative flex h-0 w-full shrink-0 overflow-hidden"
            data-test-id="product-image"
            style={{ paddingTop: aspectRatio === "portrait" ? "150%" : "100%" }}
          >
            <img
              className={cn(
                "absolute inset-0 h-full w-full object-cover object-center",
                imageClassName
              )}
              alt={name}
              src={resolvedImageSrc}
              referrerPolicy="no-referrer"
              onError={onProductImageError}
            />
          </picture>
        </Link>

        {/* Bestseller / New badges */}
        {isBestseller && (
          <span
            className="bg-fs-surface-primary absolute top-2 left-2 z-10 rounded-[2px] px-2 py-1 text-center whitespace-nowrap text-[#333333]"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.15px",
            }}
          >
            Bestseller
          </span>
        )}
        {isNew && !isBestseller && (
          <span
            className="bg-fs-surface-primary absolute top-2 left-2 z-10 rounded-[2px] px-2 py-1 text-center whitespace-nowrap text-[#333333]"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.15px",
            }}
          >
            New
          </span>
        )}

        {/* Heart – top right, no background; when onHeartClick set, click adds to staging / toggles heart */}
        <button
          className={cn(
            "absolute top-2 right-2 z-10 flex items-center justify-center p-0 transition-opacity duration-150",
            isCartTile
              ? "opacity-100"
              : onHeartClick
                ? isHearted
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100"
                : "opacity-0 group-hover:opacity-100"
          )}
          style={{
            transitionDelay: onHeartClick && isHearted ? "0ms" : "120ms",
          }}
          aria-label={isHearted ? "Remove from comparison" : "Save"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onHeartClick?.();
          }}
        >
          {isHearted ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="#921100"
              stroke="#921100"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          ) : (
            <HeartIcon />
          )}
        </button>

        {/* Find similar (left) and Add (right); cart tile: add only */}
        <div
          className={cn(
            "absolute right-2 bottom-2 left-2 z-10 flex",
            isCartTile || hideFindSimilar ? "justify-end" : "justify-between"
          )}
        >
          {!isCartTile &&
            !hideFindSimilar &&
            (findSimilarHref ? (
              <Link
                to={findSimilarHref}
                className="group/btn bg-fs-surface-primary flex h-8 items-center justify-center rounded-full border border-[#e5e5e5] pr-2 pl-2 shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-200 ease-out hover:border-[#333] hover:pr-3"
                aria-label="Find similar"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="shrink-0 text-[#333]">
                  <FindSimilarIcon />
                </span>
                <div className="max-w-0 overflow-hidden transition-all duration-200 ease-out group-hover/btn:max-w-[100px]">
                  <span className="pl-1.5 text-sm whitespace-nowrap text-[#333] opacity-0 transition-opacity duration-200 group-hover/btn:opacity-100">
                    Find similar
                  </span>
                </div>
              </Link>
            ) : (
              <button
                type="button"
                className="group/btn bg-fs-surface-primary flex h-8 items-center justify-center rounded-full border border-[#e5e5e5] pr-2 pl-2 shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-200 ease-out hover:border-[#333] hover:pr-3"
                aria-label="Find similar"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <span className="shrink-0 text-[#333]">
                  <FindSimilarIcon />
                </span>
                <div className="max-w-0 overflow-hidden transition-all duration-200 ease-out group-hover/btn:max-w-[100px]">
                  <span className="pl-1.5 text-sm whitespace-nowrap text-[#333] opacity-0 transition-opacity duration-200 group-hover/btn:opacity-100">
                    Find similar
                  </span>
                </div>
              </button>
            ))}
          <button
            type="button"
            className="bg-fs-surface-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#e5e5e5] shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-colors hover:border-[#333]"
            aria-label="Add"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onQuickAdd?.();
            }}
          >
            <PlusIcon />
          </button>
        </div>
      </div>

      {/* ── Product info ── */}
      {isCartTile ? (
        <div className="mt-3 flex flex-1 flex-col gap-1">
          {!hideWholesalePrice && (
            <div className="mb-1 flex items-baseline gap-2">
              <span
                className="text-[#333333]"
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "1.125rem",
                  fontWeight: 500,
                  lineHeight: "1.625rem",
                  letterSpacing: "0.009375rem",
                  ...(blurWholesalePrice ? { filter: "blur(4px)" } : {}),
                }}
              >
                {price}
              </span>
              {msrp && (
                <span
                  className="text-[#757575]"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.75rem",
                    lineHeight: "1rem",
                    fontWeight: 400,
                  }}
                >
                  MSRP {msrpDisplay}
                </span>
              )}
            </div>
          )}
          <Link to={productUrl} onClick={onClick}>
            <p
              className="mb-1 line-clamp-2 w-full text-[#333333]"
              data-test-id="product-tile-product-name"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: 1.4,
                letterSpacing: "0.009375rem",
              }}
            >
              {name}
            </p>
          </Link>
          {subduedDetail ? (
            <p
              className="whitespace-nowrap text-[#757575]"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
              }}
            >
              {subduedDetail}
            </p>
          ) : null}
        </div>
      ) : (
        <div
          className="mt-2 flex flex-1 flex-col"
          style={{ gap: "4px", letterSpacing: "0.15px" }}
        >
          {/* Price and title section */}
          <div className="flex flex-col" style={{ gap: "2px" }}>
            {/* Price row */}
            {!hideWholesalePrice && (
              <div
                className="flex items-baseline whitespace-nowrap"
                style={{ gap: "8px" }}
              >
                <span
                  className="text-[#333]"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "18px",
                    fontWeight: 500,
                    lineHeight: "26px",
                    ...(blurWholesalePrice ? { filter: "blur(4px)" } : {}),
                  }}
                >
                  {price}
                </span>
                {msrp && (
                  <span
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "16px",
                      color: "#333",
                    }}
                  >
                    MSRP {msrp}
                  </span>
                )}
              </div>
            )}

            {/* Product name */}
            <Link to={productUrl} onClick={onClick}>
              <p
                className="w-full overflow-hidden text-ellipsis text-[#333]"
                data-test-id="product-tile-product-name"
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "20px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {name}
              </p>
            </Link>
          </div>

          {/* Case size info */}
          <p
            className="whitespace-nowrap"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "20px",
              color: "#757575",
            }}
          >
            Case of {caseSize}
          </p>

          {shipDate ? (
            <p
              className="whitespace-nowrap"
              data-test-id="product-tile-ship-date"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "20px",
                color: "#757575",
              }}
            >
              {shipDate}
            </p>
          ) : null}

          {/* Urgency – red, 12px; compact: 11px */}
          {urgency && (
            <span
              className="font-medium"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: compact ? "0.6875rem" : "0.75rem",
                lineHeight: "1rem",
                color: "#921100",
              }}
            >
              {urgency}
            </span>
          )}

          {/* Price (V2 only - shown below product name) */}
          {hideWholesalePrice && (
            <div className="mt-1 flex items-baseline gap-2">
              <div className="flex items-baseline gap-1">
                {msrp && (
                  <>
                    <span className="text-fs-text-primary text-sm">
                      MSRP {msrp}
                    </span>
                    <span className="text-xs text-[#757575]"> • </span>
                    <span className="text-fs-text-primary text-sm">
                      Min qty 1
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {hideWholesalePrice && msrp && (
            <div
              className="flex items-baseline gap-1 text-[#757575] line-through"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: compact ? "0.8125rem" : "0.9375rem",
                lineHeight: compact ? "1.125rem" : "1.25rem",
              }}
            >
              <span>MSRP {msrp}</span>
            </div>
          )}

          {/* Promoted – 12px caption; compact: 11px */}
          {promoted && (
            <span
              className="text-[#b7b7b7]"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: compact ? "0.6875rem" : "0.75rem",
                lineHeight: "1rem",
              }}
            >
              Promoted
            </span>
          )}
        </div>
      )}
    </div>
  );
}
